/**
 * 강제 비밀번호 변경 모달 (M12 / U3).
 *
 * 관리자가 비번을 리셋하면 ``user.must_change_password === true`` 로 세팅되고,
 * 다음 로그인 직후 이 모달이 강제로 표시된다. 사용자는 변경 전에는 다른 동작을
 * 할 수 없도록 모든 닫기 경로 (배경 클릭 / ESC / X 버튼) 가 비활성화된다.
 *
 * 성공 흐름:
 *   1. /api/auth/change-password 호출 (current_password + new_password)
 *   2. 응답으로 받은 새 access/refresh 토큰을 store 에 저장 (mcp=false 클레임)
 *   3. /api/auth/me 재조회로 must_change_password=false 동기화
 *   4. 모달 자동 닫힘
 */

import { useEffect, useState } from "react";
import { AlertCircle, Loader2, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

import { changeMyPassword, fetchMe } from "@/lib/api/auth";
import { ApiError } from "@/lib/api/client";

import { useAuthStore } from "./store";

const PASSWORD_MIN_LEN = 4;

export default function ForceChangePasswordModal() {
  const { t } = useTranslation();
  const setTokens = useAuthStore((s) => s.setTokens);
  const setUser = useAuthStore((s) => s.setUser);
  const userEmail = useAuthStore((s) => s.user?.email);

  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ESC 차단 — 닫을 수 없는 모달이므로 의도하지 않은 단축키 전파 막음.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, []);

  const newPwTooShort = newPw.length > 0 && newPw.length < PASSWORD_MIN_LEN;
  const mismatch = confirmPw.length > 0 && newPw !== confirmPw;
  const sameAsCurrent = newPw.length > 0 && newPw === currentPw;
  const formValid =
    currentPw.length > 0 &&
    newPw.length >= PASSWORD_MIN_LEN &&
    newPw === confirmPw &&
    newPw !== currentPw;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formValid || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const tokens = await changeMyPassword(currentPw, newPw);
      setTokens(tokens.access_token, tokens.refresh_token);
      // 새 토큰으로 me 재조회 — must_change_password=false 가 반영된 user 갱신.
      const me = await fetchMe();
      setUser(me);
      // 성공 시 must_change_password 가 false 가 되면서 모달이 자동으로 사라진다.
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.detail
          : err instanceof Error
            ? err.message
            : t("auth.forceChange.fallbackError");
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/70 px-4 py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="force-change-pw-title"
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-2xl">
        <header className="flex items-center gap-2 border-b border-slate-200 px-5 py-4">
          <Lock className="h-5 w-5 text-amber-600" aria-hidden />
          <h2 id="force-change-pw-title" className="text-base font-semibold text-slate-900">
            {t("auth.forceChange.title")}
          </h2>
        </header>

        <div className="px-5 py-4">
          <p className="text-sm text-slate-600">{t("auth.forceChange.description")}</p>
          {userEmail ? (
            <p className="mt-1 text-xs text-slate-400">
              {t("auth.forceChange.accountLabel", { email: userEmail })}
            </p>
          ) : null}

          <form onSubmit={handleSubmit} className="mt-5 space-y-4">
            <div>
              <label htmlFor="current-pw" className="block text-xs font-medium text-slate-700">
                {t("auth.forceChange.currentLabel")}
              </label>
              <input
                id="current-pw"
                type="password"
                autoComplete="current-password"
                required
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                disabled={submitting}
              />
            </div>

            <div>
              <label htmlFor="new-pw" className="block text-xs font-medium text-slate-700">
                {t("auth.forceChange.newLabel", { count: PASSWORD_MIN_LEN })}
              </label>
              <input
                id="new-pw"
                type="password"
                autoComplete="new-password"
                required
                minLength={PASSWORD_MIN_LEN}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                disabled={submitting}
                aria-invalid={newPwTooShort || sameAsCurrent}
              />
              {newPwTooShort ? (
                <p className="mt-1 text-xs text-red-600">
                  {t("auth.forceChange.tooShort", { count: PASSWORD_MIN_LEN })}
                </p>
              ) : sameAsCurrent ? (
                <p className="mt-1 text-xs text-red-600">{t("auth.forceChange.sameAsCurrent")}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="confirm-pw" className="block text-xs font-medium text-slate-700">
                {t("auth.forceChange.confirmLabel")}
              </label>
              <input
                id="confirm-pw"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                disabled={submitting}
                aria-invalid={mismatch}
              />
              {mismatch ? (
                <p className="mt-1 text-xs text-red-600">{t("auth.forceChange.mismatch")}</p>
              ) : null}
            </div>

            {error ? (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            ) : null}

            <button
              type="submit"
              disabled={!formValid || submitting}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
              {t("auth.forceChange.submit")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
